import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { haptics } from '../telegram.js'

/**
 * The one and only full-width tap-target button used across screens.
 * Variants share padding, border-radius, and active scale so they feel
 * like part of the same family.
 *
 * Variants:
 *  - `gradient`  — brand-gradient-hero with white text + heavy shadow.
 *                  The signature primary CTA, matches the hero cards on
 *                  Home so "Practice again" reads as a continuation of
 *                  the same visual language.
 *  - `primary`   — solid green button (brand-primary-strong). Use as a
 *                  quieter primary when a screen already has a gradient
 *                  hero card and you don't want two attention-grabbers.
 *  - `secondary` — bordered surface button. Use for "Back to home" or
 *                  any alternative action that shouldn't compete.
 *  - `subtle`    — text-only button. Use for tertiary "or exit" links.
 *
 * Every variant fires a haptic tap on press by default — turn off with
 * `haptic={false}` for cases where the parent already handles it.
 */

export type ButtonVariant = 'gradient' | 'primary' | 'secondary' | 'subtle'
export type ButtonSize = 'md' | 'sm'

interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  variant?: ButtonVariant
  size?: ButtonSize
  /** When true, fires haptics.medium() (primary/gradient) or haptics.light() on click. */
  haptic?: boolean
  /** Optional leading icon. */
  leadingIcon?: ReactNode
  /** Optional trailing icon. */
  trailingIcon?: ReactNode
  className?: string
  children: ReactNode
}

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  gradient:
    'bg-brand-gradient-hero text-brand-on-primary font-semibold shadow-brand-lg active:scale-[0.99]',
  primary:
    'bg-brand-primary-strong text-brand-on-primary font-semibold shadow-brand active:scale-[0.99]',
  secondary:
    'bg-brand-surface text-brand-text font-medium border border-brand-border active:scale-[0.99]',
  subtle: 'text-brand-text-muted font-medium active:opacity-50',
}

const SIZE_CLASSES: Record<ButtonSize, string> = {
  md: 'rounded-[14px] py-3.5 px-5 text-sm',
  sm: 'rounded-xl py-2.5 px-4 text-xs',
}

export function Button({
  variant = 'gradient',
  size = 'md',
  haptic = true,
  leadingIcon,
  trailingIcon,
  onClick,
  children,
  className = '',
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      onClick={(e) => {
        if (disabled) return
        if (haptic) {
          if (variant === 'gradient' || variant === 'primary') haptics.medium()
          else haptics.light()
        }
        onClick?.(e)
      }}
      disabled={disabled}
      className={`${SIZE_CLASSES[size]} ${VARIANT_CLASSES[variant]} ${
        disabled ? 'opacity-40 cursor-not-allowed' : 'transition-transform'
      } inline-flex items-center justify-center gap-2 ${className}`}
      {...rest}
    >
      {leadingIcon}
      {children}
      {trailingIcon}
    </button>
  )
}
